import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, User, Building, Car, 
  Mail, Users, ShieldCheck, Sparkles, Loader2, Keyboard as KeyboardIcon,
  HelpCircle, Check
} from 'lucide-react';
import SignatureCanvas from './SignatureCanvas';
import VirtualKeyboard from './VirtualKeyboard';

export default function CheckInFlow({ onCancel, onComplete }) {
  // Steps:
  // 1: Lookup
  // 2: Name
  // 3: Company
  // 4: Vehicle Reg
  // 5: Email
  // 6: Host (Predictive Search + Other)
  // 7: Safety & Signature
  // 8: Success
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hosts, setHosts] = useState([]);
  
  // Search / Returning state
  const [searchQuery, setSearchQuery] = useState('');
  const [isReturning, setIsReturning] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [registration, setRegistration] = useState('');
  const [email, setEmail] = useState('');
  const [visitingHost, setVisitingHost] = useState('');
  const [hostSearch, setHostSearch] = useState('');
  const [isOtherHost, setIsOtherHost] = useState(false);
  const [otherHostText, setOtherHostText] = useState('');
  
  const [agreedSafety, setAgreedSafety] = useState(true);
  const [signature, setSignature] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [activeInput, setActiveInput] = useState(null);

  // Result state
  const [countdown, setCountdown] = useState(8);

  useEffect(() => {
    // Load staff hosts
    axios.get('/api/visitors/hosts')
      .then((res) => {
        setHosts(res.data || []);
      })
      .catch((err) => console.error('Error fetching hosts:', err));
  }, []);

  // Countdown timer on step 8
  useEffect(() => {
    if (step === 8) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, onComplete]);

  // Handle Quick Lookup
  const handleLookup = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) {
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`/api/visitors/lookup?q=${encodeURIComponent(searchQuery.trim())}`);
      if (res.data && res.data.found && res.data.visitor) {
        const v = res.data.visitor;
        setName(v.name || '');
        setEmail(v.email || '');
        setRegistration(v.registration || '');
        setCompany(v.company || '');
        if (v.visiting_host) setVisitingHost(v.visiting_host);
        setIsReturning(true);
      } else {
        if (searchQuery.includes('@')) {
          setEmail(searchQuery.trim());
        } else {
          setRegistration(searchQuery.trim().toUpperCase());
        }
      }
    } catch (err) {
      console.warn('Lookup failed, proceeding:', err);
    } finally {
      setLoading(false);
      setStep(2);
    }
  };

  const handleCompleteCheckIn = async () => {
    if (!signature) {
      return alert('Please sign inside the signature box to complete your sign-in.');
    }
    if (!agreedSafety) {
      return alert('Please confirm agreement to site health & safety rules.');
    }

    setLoading(true);
    try {
      const finalHost = isOtherHost ? (otherHostText.trim() || 'General Visitor / Delivery') : visitingHost;
      const payload = {
        name: name.trim(),
        email: email.trim(),
        registration: registration.trim().toUpperCase(),
        company: company.trim(),
        visiting_host: finalHost,
        signature: signature,
        agreed_safety: agreedSafety,
      };

      await axios.post('/api/visitors/check-in', payload);
      setShowKeyboard(false);
      setStep(8);
    } catch (err) {
      console.error('Check-in error:', err);
      alert(err?.response?.data?.detail || 'Failed to complete check-in. Please see reception.');
    } finally {
      setLoading(false);
    }
  };

  // Filter staff hosts for predictive search
  const filteredHosts = hosts.filter((h) => {
    const q = hostSearch.toLowerCase().trim();
    if (!q) return true;
    return (
      (h.name || '').toLowerCase().includes(q) ||
      (h.email || '').toLowerCase().includes(q)
    );
  });

  // Virtual keyboard input handlers
  const handleVirtualKeyPress = (char) => {
    if (activeInput === 'search') setSearchQuery((p) => p + char);
    else if (activeInput === 'name') setName((p) => p + char);
    else if (activeInput === 'company') setCompany((p) => p + char);
    else if (activeInput === 'registration') setRegistration((p) => (p + char).toUpperCase());
    else if (activeInput === 'email') setEmail((p) => p + char);
    else if (activeInput === 'hostSearch') setHostSearch((p) => p + char);
    else if (activeInput === 'otherHost') setOtherHostText((p) => p + char);
  };

  const handleVirtualBackspace = () => {
    if (activeInput === 'search') setSearchQuery((p) => p.slice(0, -1));
    else if (activeInput === 'name') setName((p) => p.slice(0, -1));
    else if (activeInput === 'company') setCompany((p) => p.slice(0, -1));
    else if (activeInput === 'registration') setRegistration((p) => p.slice(0, -1));
    else if (activeInput === 'email') setEmail((p) => p.slice(0, -1));
    else if (activeInput === 'hostSearch') setHostSearch((p) => p.slice(0, -1));
    else if (activeInput === 'otherHost') setOtherHostText((p) => p.slice(0, -1));
  };

  const handleVirtualClear = () => {
    if (activeInput === 'search') setSearchQuery('');
    else if (activeInput === 'name') setName('');
    else if (activeInput === 'company') setCompany('');
    else if (activeInput === 'registration') setRegistration('');
    else if (activeInput === 'email') setEmail('');
    else if (activeInput === 'hostSearch') setHostSearch('');
    else if (activeInput === 'otherHost') setOtherHostText('');
  };

  const totalSteps = 7;
  const progressPercent = Math.min(100, Math.round(((step - 1) / (totalSteps - 1)) * 100));

  return (
    <div className="w-full max-w-xl mx-auto flex flex-col justify-center min-h-[85vh] p-3 sm:p-4">
      {/* Progress Bar (Only during steps 1 to 7) */}
      {step < 8 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-slate-400 font-medium mb-1.5 px-1">
            <span>Visitor Sign-In</span>
            <span className="text-cyan-400 font-bold">Step {step} of {totalSteps}</span>
          </div>
          <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-teal-400 transition-all duration-300 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* STEP 1: QUICK LOOKUP */}
      {step === 1 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Cancel
            </button>
            <span className="text-[11px] uppercase tracking-wider text-cyan-400 font-bold">Check In</span>
          </div>

          <div className="text-center space-y-1.5">
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">Welcome to JSD IT</h2>
            <p className="text-slate-400 text-sm">
              Visited us before? Enter your Vehicle Reg or Email to fast-track sign in.
            </p>
          </div>

          <form onSubmit={handleLookup} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onFocus={() => setActiveInput('search')}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g. AF19 AVR or name@company.com"
                className="w-full py-4 pl-4 pr-12 text-lg font-medium rounded-2xl bg-slate-900 border-2 border-cyan-500/40 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="absolute right-3 top-3.5 p-1.5 rounded-lg text-slate-400 hover:text-cyan-400"
              >
                <KeyboardIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col gap-2.5 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 text-base font-bold shadow-lg flex items-center justify-center gap-2 touch-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <span>Next</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 font-semibold text-xs border border-white/10 touch-btn"
              >
                I'm a First-Time Visitor
              </button>
            </div>
          </form>
        </div>
      )}

      {/* STEP 2: FULL NAME */}
      {step === 2 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {isReturning && (
              <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-bold">
                <Sparkles className="w-3 h-3" /> Auto-Filled
              </span>
            )}
          </div>

          <div className="space-y-1.5 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-2">
              <User className="w-6 h-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">What is your full name?</h2>
            <p className="text-slate-400 text-xs sm:text-sm">Please enter your first and last name for our visitor badge record.</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) setStep(3); else alert('Please enter your name'); }} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                required
                value={name}
                onFocus={() => setActiveInput('name')}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. John Doe"
                className="w-full py-4 pl-4 pr-12 text-xl font-bold rounded-2xl bg-slate-900 border border-white/20 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 shadow-inner"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="absolute right-3 top-3.5 p-1.5 rounded-lg text-slate-400 hover:text-cyan-400"
              >
                <KeyboardIcon className="w-5 h-5" />
              </button>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 text-base font-bold shadow-lg flex items-center justify-center gap-2 touch-btn"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* STEP 3: COMPANY / ORGANISATION */}
      {step === 3 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-[11px] text-slate-400">Optional</span>
          </div>

          <div className="space-y-1.5 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-2">
              <Building className="w-6 h-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">What company are you with?</h2>
            <p className="text-slate-400 text-xs sm:text-sm">Leave blank if you are here for a personal appointment or delivery.</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setStep(4); }} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={company}
                onFocus={() => setActiveInput('company')}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. Acme Tech / Freelance"
                className="w-full py-4 pl-4 pr-12 text-lg font-bold rounded-2xl bg-slate-900 border border-white/20 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 shadow-inner"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="absolute right-3 top-3.5 p-1.5 rounded-lg text-slate-400 hover:text-cyan-400"
              >
                <KeyboardIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <button
                type="submit"
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 text-base font-bold shadow-lg flex items-center justify-center gap-2 touch-btn"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => { setCompany(''); setStep(4); }}
                className="w-full py-2.5 text-xs text-slate-400 hover:text-white"
              >
                Skip / Private Visitor
              </button>
            </div>
          </form>
        </div>
      )}

      {/* STEP 4: VEHICLE REGISTRATION */}
      {step === 4 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={() => setStep(3)}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-[11px] text-slate-400">Car Park Registration</span>
          </div>

          <div className="space-y-1.5 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-2">
              <Car className="w-6 h-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">Are you parked in our car park?</h2>
            <p className="text-slate-400 text-xs sm:text-sm">Enter your vehicle registration to register your parking space.</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setStep(5); }} className="space-y-4">
            <div className="flex rounded-2xl overflow-hidden border-2 border-white/20 focus-within:border-amber-400 shadow-inner">
              <div className="uk-plate-band px-3 flex flex-col items-center justify-center text-xs font-bold">
                <span>GB</span>
              </div>
              <input
                type="text"
                value={registration}
                onFocus={() => setActiveInput('registration')}
                onChange={(e) => setRegistration(e.target.value.toUpperCase())}
                placeholder="e.g. AF19 AVR"
                className="w-full py-4 px-3 bg-slate-900 text-amber-300 font-mono font-black text-xl tracking-wider uppercase focus:outline-none"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="px-3 bg-slate-900 text-slate-400 hover:text-amber-400"
              >
                <KeyboardIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <button
                type="submit"
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 text-base font-bold shadow-lg flex items-center justify-center gap-2 touch-btn"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => { setRegistration(''); setStep(5); }}
                className="w-full py-2.5 text-xs text-slate-400 hover:text-white"
              >
                I didn't drive / No vehicle
              </button>
            </div>
          </form>
        </div>
      )}

      {/* STEP 5: EMAIL ADDRESS */}
      {step === 5 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={() => setStep(4)}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-[11px] uppercase tracking-wider text-cyan-400 font-bold">Contact</span>
          </div>

          <div className="space-y-1.5 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-400 mb-2">
              <Mail className="w-6 h-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">What is your email address?</h2>
            <p className="text-slate-400 text-xs sm:text-sm">Used for visitor security log and instant recognition on return.</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) setStep(6); else alert('Please enter your email'); }} className="space-y-4">
            <div className="relative">
              <input
                type="email"
                required
                value={email}
                onFocus={() => setActiveInput('email')}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@company.com"
                className="w-full py-4 pl-4 pr-12 text-lg font-medium rounded-2xl bg-slate-900 border border-white/20 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 shadow-inner"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="absolute right-3 top-3.5 p-1.5 rounded-lg text-slate-400 hover:text-cyan-400"
              >
                <KeyboardIcon className="w-5 h-5" />
              </button>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 text-base font-bold shadow-lg flex items-center justify-center gap-2 touch-btn"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* STEP 6: WHO ARE YOU VISITING? (PREDICTIVE SEARCH + OTHER) */}
      {step === 6 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-5 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={() => setStep(5)}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-[11px] uppercase tracking-wider text-cyan-400 font-bold">Select Host</span>
          </div>

          <div className="space-y-1 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-2">
              <Users className="w-6 h-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">Who are you visiting today?</h2>
            <p className="text-slate-400 text-xs sm:text-sm">Start typing their name or select them below.</p>
          </div>

          {/* Predictive Search Input */}
          <div className="space-y-3">
            <div className="relative">
              <input
                type="text"
                value={hostSearch}
                onFocus={() => setActiveInput('hostSearch')}
                onChange={(e) => {
                  setHostSearch(e.target.value);
                  setIsOtherHost(false);
                }}
                placeholder="Type to search staff name..."
                className="w-full py-3.5 pl-4 pr-12 text-base font-medium rounded-xl bg-slate-900 border-2 border-cyan-500/40 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="absolute right-3 top-3 p-1 rounded-lg text-slate-400 hover:text-cyan-400"
              >
                <KeyboardIcon className="w-5 h-5" />
              </button>
            </div>

            {/* List of matching staff cards */}
            <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
              {filteredHosts.map((h) => {
                const isSelected = !isOtherHost && visitingHost === h.name;
                return (
                  <button
                    key={h.id || h.email}
                    type="button"
                    onClick={() => {
                      setVisitingHost(h.name);
                      setIsOtherHost(false);
                      setStep(7); // Auto-advance to signature!
                    }}
                    className={`w-full p-3 rounded-xl text-left border flex items-center justify-between transition-all touch-btn ${
                      isSelected
                        ? 'bg-cyan-500/20 border-cyan-400 text-white shadow-md'
                        : 'bg-slate-900/80 border-white/10 text-slate-200 hover:border-cyan-500/50 hover:bg-slate-850'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center font-bold text-cyan-400 text-xs">
                        {h.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-sm text-white">{h.name}</div>
                        <div className="text-[11px] text-slate-400">{h.email}</div>
                      </div>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-cyan-400" />}
                  </button>
                );
              })}

              {/* "Other / Not Listed" Button */}
              <button
                type="button"
                onClick={() => {
                  setIsOtherHost(true);
                  setVisitingHost('Other');
                }}
                className={`w-full p-3 rounded-xl text-left border flex items-center justify-between transition-all touch-btn ${
                  isOtherHost
                    ? 'bg-amber-500/20 border-amber-400 text-white shadow-md'
                    : 'bg-slate-900/60 border-dashed border-white/20 text-slate-400 hover:text-white hover:border-white/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-xs">
                    <HelpCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-amber-300">Other / Not Listed / Delivery</div>
                    <div className="text-[11px] text-slate-400">Tap here to type a custom person or reason</div>
                  </div>
                </div>
                {isOtherHost && <Check className="w-4 h-4 text-amber-400" />}
              </button>
            </div>

            {/* Custom Other Text Input */}
            {isOtherHost && (
              <div className="p-3 rounded-xl bg-slate-900/90 border border-amber-500/40 space-y-2 animate-in fade-in duration-200">
                <label className="text-xs font-semibold text-amber-300">
                  Please specify who or what you are visiting:
                </label>
                <input
                  type="text"
                  value={otherHostText}
                  onFocus={() => setActiveInput('otherHost')}
                  onChange={(e) => setOtherHostText(e.target.value)}
                  placeholder="e.g. Parcel Delivery, Accounts, Interview..."
                  className="w-full py-2.5 px-3 rounded-lg bg-slate-950 border border-white/20 text-white text-sm focus:outline-none focus:border-amber-400"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!otherHostText.trim()) return alert('Please enter who or what you are visiting');
                    setStep(7);
                  }}
                  className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow flex items-center justify-center gap-1.5 touch-btn mt-1"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STEP 7: HEALTH & SAFETY + SIGNATURE */}
      {step === 7 && (
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-5 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <button
              onClick={() => setStep(6)}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-[11px] uppercase tracking-wider text-cyan-400 font-bold">Final Step</span>
          </div>

          <div className="space-y-1 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-black text-white font-display">Health & Safety</h2>
            <p className="text-slate-400 text-xs">Please review rules and sign using your finger.</p>
          </div>

          {/* Compact site rules */}
          <div className="p-3.5 rounded-xl bg-slate-900/90 border border-white/10 text-xs text-slate-300 space-y-1">
            <div className="flex items-center gap-1.5 text-amber-400 font-bold text-xs">
              <ShieldCheck className="w-4 h-4" /> Fire Safety Notice
            </div>
            <p className="text-slate-400 text-[11px] leading-relaxed">
              In an emergency, follow green exit signs to the main car park assembly area. Please stay with your host (<strong className="text-cyan-400">{isOtherHost ? otherHostText : visitingHost}</strong>).
            </p>
          </div>

          {/* Digital Signature */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-300">
              Digital Signature <span className="text-red-400">*</span>
            </label>
            <SignatureCanvas onSave={setSignature} />
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={handleCompleteCheckIn}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 text-lg font-bold shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-2 touch-btn"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Complete Sign In</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* STEP 8: ARRIVAL CONFIRMATION */}
      {step === 8 && (
        <div className="glass-panel-glow rounded-3xl p-8 text-center space-y-6 animate-in zoom-in-95 duration-300">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 mx-auto flex items-center justify-center text-emerald-400 shadow-xl shadow-emerald-500/20 animate-bounce">
            <CheckCircle2 className="w-12 h-12" />
          </div>

          <div className="space-y-1.5">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-display">
              Welcome, {name}!
            </h2>
            <p className="text-base text-emerald-300 font-medium">
              You are signed in to JSD IT.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-white/10 max-w-sm mx-auto text-xs text-slate-300 space-y-1">
            <div>Visiting: <strong className="text-cyan-400">{isOtherHost ? otherHostText : visitingHost}</strong></div>
            {registration && <div>Vehicle Reg: <strong className="text-amber-300 font-mono">{registration}</strong></div>}
            <div className="text-[11px] text-slate-400 pt-2 border-t border-white/10 mt-2">
              Your host has been notified. Please take a seat in reception and they will be with you shortly.
            </div>
          </div>

          <div className="pt-2 flex justify-center">
            <button
              onClick={onComplete}
              className="px-8 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs border border-white/10 touch-btn"
            >
              Done (Returning in {countdown}s)
            </button>
          </div>
        </div>
      )}

      {/* Virtual Keyboard Drawer */}
      {showKeyboard && (
        <VirtualKeyboard
          onKeyPress={handleVirtualKeyPress}
          onBackspace={handleVirtualBackspace}
          onClear={handleVirtualClear}
          onClose={() => setShowKeyboard(false)}
        />
      )}
    </div>
  );
}
